/**
 * Cart Reward Progress System
 * Handles milestone tracking, free product add/remove, abuse prevention,
 * and progress bar UI updates for the cart drawer.
 *
 * HOW THE REWARDS ARE MADE FREE:
 *   - "Free Gift" is a ₹0 product (compare-at ₹400 for the strikethrough). Keep
 *     it at ₹0: pricing it and freeing it with a second "Spend ₹X, get 1 free"
 *     discount clashes with the stand's — Shopify won't count the same spend
 *     toward both, so between ₹2,499 and ~₹3,989 one reward was charged.
 *   - The stand is zeroed by "Free Ceramic Stand on ₹2499+"; the stand variant
 *     must belong to the "Ceramic Stands – Free Gift" collection it targets.
 * The discount frees only one stand, so the cart must never hold more than one
 * of each reward. reconcileRewards() enforces exactly 0 or 1 per reward, and
 * dropRewardsNotZeroed() removes any reward line Shopify did not actually zero
 * (expired/misconfigured discount). Because the gift is ₹0, ANY line of it —
 * even one added from its product page — is managed here, so nobody gets it
 * below the threshold.
 *
 * Thresholds must match the discounts' minimum spend AND
 * snippets/cart-reward-progress.liquid (server render of the same bar).
 */

// ─── Configuration ────────────────────────────────────────────────────────────
const REWARDS_CONFIG = {
  // Thresholds are in paise (Shopify stores money × 100). ₹250 = 25000.
  // `position` is where the milestone dot sits on the track (%). The fill is
  // interpolated between dots so it reaches a dot exactly at its threshold.
  milestones: [
    {
      threshold: 25000,
      position: 10,
      label: 'Free Delivery',
      type: 'visual', // no product added — just a UI milestone
    },
    {
      threshold: 149000,
      position: 55,
      label: 'Free Gift',
      type: 'product',
      variantId: 44028726345816, // "Free Gift" product (₹0)
      giftOnly: true, // never sold on its own — every line of it is managed as the reward
    },
    {
      threshold: 249900,
      position: 85,
      label: 'Free Designer Stand',
      type: 'product',
      // "Havan Cup Stand – Teal Floral Ceramic Base" from the "Ceramic Stands – Free Gift"
      // collection; made free at checkout by the "Free Ceramic Stand on ₹2499+" auto discount.
      variantId: 44347154301016,
    },
  ],
  maxThreshold: 249900,
  freeRewardProperty: '_free_reward', // line item property key used to identify free items
};

// ─── State ────────────────────────────────────────────────────────────────────
let _isProcessing = false; // guards direct (unqueued) calls from overlapping
let _validationQueued = false; // coalesces bursts of cart events into one pass
let _queuedCart = null; // newest FULL cart handed in by the events of the queued pass
// variantId → paid total at which adding/zeroing that reward failed. We don't
// retry at the same total (no API hammering), but do once the cart changes.
const _failedRewardVariants = new Map();

// ─── Utilities ────────────────────────────────────────────────────────────────

// X-Cart-Internal keeps cart-drawer.js's optimistic-open hook off our own adds.
const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Cart-Internal': '1' };
const RENDER_SECTIONS = 'cart-drawer,cart-icon-bubble';

/**
 * POST a reward change and get the re-rendered drawer back in the same
 * response, so no separate /cart.js re-read or section fetch is needed.
 * @returns {Promise<object|null>} the response (with `sections`), or null on failure
 */
async function postCart(path, body) {
  const res = await fetch(path, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ ...body, sections: RENDER_SECTIONS, sections_url: window.location.pathname }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.status) {
    console.error('[CartRewards] ' + path + ' failed:', data);
    return null;
  }
  return data;
}

function isRewardLine(item) {
  return !!(item.properties && item.properties[REWARDS_CONFIG.freeRewardProperty] === 'true');
}

/**
 * Sum line prices for all cart items that are NOT free reward products.
 * @param {Array} items - cart.items array from /cart.js response
 * @returns {number} paid subtotal in paise
 */
function calculateCartTotalExcludingFreeItems(items) {
  return items.reduce((total, item) => (isRewardLine(item) ? total : total + item.line_price), 0);
}

/**
 * Fetch the latest cart state from Shopify.
 * @returns {Promise<object>} cart object
 */
async function fetchCart() {
  const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  return response.json();
}

/**
 * Map a paid total to a fill % so the bar hits each dot exactly at its threshold.
 * @param {number} paidTotal - paise
 * @returns {number} 0–100
 */
function rewardFillPct(paidTotal) {
  if (paidTotal <= 0) return 0;
  if (paidTotal >= REWARDS_CONFIG.maxThreshold) return 100;
  let prevThreshold = 0;
  let prevPos = 0;
  for (const m of REWARDS_CONFIG.milestones) {
    if (paidTotal < m.threshold) {
      const span = (paidTotal - prevThreshold) / (m.threshold - prevThreshold);
      return prevPos + span * (m.position - prevPos);
    }
    prevThreshold = m.threshold;
    prevPos = m.position;
  }
  return 100;
}

// ─── Core Logic ───────────────────────────────────────────────────────────────

/**
 * Bring the cart to exactly the right reward state in as few requests as
 * possible: 1 unit of each earned reward, 0 of each unearned one, duplicates
 * collapsed. Idempotent — running it twice on the same cart changes nothing.
 * @param {Array} items - FRESH cart items
 * @param {number} paidTotal - paise
 * @returns {Promise<{latest: object|null, lines: Array}>} `latest` is the last
 *   successful response (carries the rendered sections; null if nothing
 *   changed); `lines` are the server's post-change lines to price-check.
 */
async function reconcileRewards(items, paidTotal) {
  const updates = {}; // line key → quantity, applied in one /cart/update.js call
  const toAdd = [];

  // Abuse prevention: cart has items but ALL of them are free → purge rewards.
  const purgeAll = items.length > 0 && paidTotal === 0;

  for (const milestone of REWARDS_CONFIG.milestones) {
    if (milestone.type !== 'product' || !milestone.variantId) continue;

    const failedAt = _failedRewardVariants.get(milestone.variantId);
    const blocked = failedAt !== undefined && failedAt === paidTotal;
    const desired = !purgeAll && !blocked && paidTotal >= milestone.threshold ? 1 : 0;

    // Group this reward's lines by key. A discount can split one line of
    // quantity 2 into a ₹0 line and a full-price line, so add up the quantity
    // for each key instead of assuming there is one line per key.
    const qtyByKey = new Map();
    items.forEach((item) => {
      if (item.variant_id !== milestone.variantId) return;
      if (isRewardLine(item)) {
        qtyByKey.set(item.key, (qtyByKey.get(item.key) || 0) + item.quantity);
      } else if (milestone.giftOnly) {
        // Added outside the reward flow (e.g. from its product page): drop it
        // and, if earned, re-add a proper reward line below.
        updates[item.key] = 0;
      }
    });

    if (qtyByKey.size === 0) {
      if (desired === 1) toAdd.push(milestone.variantId);
      continue;
    }

    let remaining = desired;
    qtyByKey.forEach((qty, key) => {
      const keep = Math.min(qty, remaining);
      remaining -= keep;
      if (keep !== qty) updates[key] = keep;
    });
  }

  let latest = null;
  let lines = items;

  if (Object.keys(updates).length > 0) {
    const res = await postCart('/cart/update.js', { updates });
    if (res) {
      latest = res;
      lines = res.items || []; // update.js returns the whole cart
    }
  }

  if (toAdd.length > 0) {
    const properties = { [REWARDS_CONFIG.freeRewardProperty]: 'true' };
    const res = await postCart('/cart/add.js', { items: toAdd.map((id) => ({ id, quantity: 1, properties })) });
    if (res) {
      latest = res;
      // add.js returns only the added lines (with their discounted prices).
      const addedKeys = new Set((res.items || []).map((i) => i.key));
      lines = lines.filter((i) => !addedKeys.has(i.key)).concat(res.items || []);
    } else {
      // e.g. sold out — skip at this total so we don't retry on every event.
      toAdd.forEach((id) => _failedRewardVariants.set(id, paidTotal));
    }
  }

  return { latest, lines };
}

/**
 * Verify every reward line is actually free at the server and remove any that
 * isn't. This guards against a broken/expired automatic discount (or a total
 * that Shopify counts differently from the bar) silently charging the customer
 * for a "free" item. Uses `final_line_price` (post-discount), never the
 * client-set `_free_reward` property, as proof of price.
 * @param {Array} items - FRESH cart items (after reconcileRewards)
 * @param {number} paidTotal - paise
 * @returns {Promise<object|null>} the removal response (with sections), or null
 */
async function dropRewardsNotZeroed(items, paidTotal) {
  const updates = {};
  items.forEach((item) => {
    if (isRewardLine(item) && item.final_line_price > 0) {
      console.warn(
        '[CartRewards] Reward line NOT zeroed by its discount (final_line_price=' +
          item.final_line_price + ' paise) — removing to avoid overcharge. variant=' + item.variant_id +
          '. Check the backing automatic discount is ACTIVE in Shopify admin.'
      );
      updates[item.key] = 0;
      _failedRewardVariants.set(item.variant_id, paidTotal);
    }
  });
  if (Object.keys(updates).length === 0) return null;
  return postCart('/cart/update.js', { updates });
}

/**
 * A whole cart, as returned by /cart.js, /cart/change.js or /cart/update.js.
 * NOT an /cart/add.js response: its `items` holds only the lines just added.
 * Reading that as the cart made the gift look missing and added a second,
 * full-price unit — so add responses are never trusted here.
 */
function isFullCart(data) {
  return !!(data && Array.isArray(data.items) && typeof data.item_count === 'number' && 'total_price' in data);
}

/**
 * Main orchestrator. Uses `freshCart` when the caller just received the whole
 * cart from the server (a quantity change), otherwise reads /cart.js.
 *
 * Runs as a cartOpsQueue task; never call scheduleRewardValidation() from inside it.
 * @param {object} [freshCart] - a full cart that reflects every change so far
 */
async function validateCartRewards(freshCart) {
  if (_isProcessing) return;
  _isProcessing = true;

  try {
    const cart = isFullCart(freshCart) ? freshCart : await fetchCart();
    const paidTotal = calculateCartTotalExcludingFreeItems(cart.items || []);

    const { latest, lines } = await reconcileRewards(cart.items || [], paidTotal);

    // Always check — also cleans up carts that already hold a charged "free" line.
    const removal = await dropRewardsNotZeroed(lines, paidTotal);

    // Paint the drawer from the last change's own response (no extra fetch).
    const rendered = removal || latest;
    if (rendered && rendered.sections) renderDrawerSections(rendered.sections);

    updateRewardBarUI(paidTotal);

    // Verify automatic discounts for 2/4/5/6 items
    validateAutomaticDiscounts(cart);
  } catch (err) {
    console.error('[CartRewards] validateCartRewards error:', err);
  } finally {
    _isProcessing = false;
  }
}

/**
 * Queue one reward pass behind any in-flight cart cycle. A burst of events
 * (drawer open + cartUpdate publish from the same add) collapses into one pass
 * that sees the final cart.
 * @param {object} [cartData] - the event's payload; reused only when it is a
 *   full cart AND the newest event of the burst (anything else → fresh read)
 */
function scheduleRewardValidation(cartData) {
  // The queue runs in order, so the latest event's cart is the freshest one.
  // An event without a full cart means the cart changed some other way.
  _queuedCart = isFullCart(cartData) ? cartData : null;
  if (_validationQueued) return;
  _validationQueued = true;
  const task = () => {
    _validationQueued = false; // events arriving mid-run schedule a fresh pass
    const cart = _queuedCart;
    _queuedCart = null;
    return validateCartRewards(cart);
  };
  if (window.cartOpsQueue) window.cartOpsQueue.enqueue(task);
  else task();
}
window.scheduleRewardValidation = scheduleRewardValidation;

// ─── UI Updates ───────────────────────────────────────────────────────────────

/**
 * Update all DOM elements of the reward progress bar.
 * @param {number} paidTotal - paid subtotal in paise
 */
function updateRewardBarUI(paidTotal) {
  const bar = document.getElementById('cart-reward-bar');
  if (!bar) return;

  const fill = document.getElementById('cart-reward-fill');
  const message = document.getElementById('cart-reward-message');
  const trackDots = bar.querySelectorAll('.cart-reward__track-dot');
  const milestoneEls = bar.querySelectorAll('.cart-reward__milestone');
  const { milestones, maxThreshold } = REWARDS_CONFIG;

  const pct = rewardFillPct(paidTotal);
  if (fill) {
    const previousPct = Number(bar.dataset.fillPct || 0);

    // Keep a tiny visible fill when cart has value so progress is always noticeable.
    const visiblePct = paidTotal > 0 ? Math.max(pct, 2) : 0;

    if (pct > previousPct) {
      fill.classList.add('is-increasing');
      window.clearTimeout(fill._increaseAnimTimeout);
      fill._increaseAnimTimeout = window.setTimeout(() => {
        fill.classList.remove('is-increasing');
      }, 850);
    }

    fill.style.width = `${visiblePct}%`;
  }

  // Milestone track-dot + label states
  milestones.forEach((milestone, index) => {
    const achieved = paidTotal >= milestone.threshold;
    if (trackDots[index]) {
      trackDots[index].classList.toggle('is-achieved', achieved);
      trackDots[index].innerHTML = achieved ? '<span class="cart-reward__check-icon">✓</span>' : '';
    }
    if (milestoneEls[index]) milestoneEls[index].classList.toggle('is-achieved', achieved);
  });

  // Dynamic message
  if (message) {
    if (paidTotal >= maxThreshold) {
      message.innerHTML = "You've unlocked all rewards! 🎉";
    } else {
      const next = milestones.find((m) => paidTotal < m.threshold);
      if (next) {
        const rupees = Math.ceil((next.threshold - paidTotal) / 100).toLocaleString('en-IN');
        message.innerHTML = `You are <strong>₹${rupees}</strong> away from <strong>${next.label}</strong>!`;
      }
    }
  }

  bar.dataset.cartTotal = paidTotal;
  bar.dataset.fillPct = pct;
}

// ─── Cart Drawer Refresh ───────────────────────────────────────────────────────

/**
 * Slide the reward bar to its new state instead of snapping: keep the old fill
 * width on the fresh bar for one frame, then let the CSS transition run.
 * Used by cart-drawer.js renderContents as well.
 */
function swapRewardBar(target, fresh) {
  const oldFill = target.querySelector('#cart-reward-fill');
  const newFill = fresh.querySelector('#cart-reward-fill');
  const toWidth = newFill && newFill.style.width;
  if (oldFill && newFill) newFill.style.width = oldFill.style.width;
  target.replaceWith(fresh);
  if (oldFill && newFill && toWidth !== oldFill.style.width) {
    void newFill.offsetWidth; // commit the starting width before animating
    newFill.style.width = toWidth;
  }
}
window.swapRewardBar = swapRewardBar;

/**
 * Paint freshly rendered drawer + cart-bubble sections (e.g. from a
 * /cart/add.js or /cart/update.js response that asked for `sections`).
 * @param {object} sections - { 'cart-drawer': html, 'cart-icon-bubble': html }
 */
function renderDrawerSections(sections) {
  const drawerText = sections['cart-drawer'];
  const bubbleText = sections['cart-icon-bubble'];
  if (drawerText) {
    const html = new DOMParser().parseFromString(drawerText, 'text/html');

    const itemsTarget = document.querySelector('#CartDrawer-CartItems');
    const itemsFresh = html.querySelector('#CartDrawer-CartItems');

    if (itemsTarget && itemsFresh) {
      // Morph the item rows in place (only the free-reward row is actually
      // added/removed) and swap just the totals + progress bar. Leaving the
      // upsell/recommendations untouched avoids replaying their animations,
      // and unchanged product rows keep their images without flashing.
      if (window.morphCartItemsContents) {
        window.morphCartItemsContents(itemsTarget, itemsFresh.innerHTML);
      } else {
        itemsTarget.innerHTML = itemsFresh.innerHTML;
      }
      const barTarget = document.querySelector('#cart-reward-bar');
      const barFresh = html.querySelector('#cart-reward-bar');
      if (barTarget && barFresh) swapRewardBar(barTarget, barFresh);
      const footTarget = document.querySelector('.cart-drawer__footer');
      const footFresh = html.querySelector('.cart-drawer__footer');
      if (footTarget && footFresh) footTarget.replaceWith(footFresh);
    } else {
      // Structure changed (e.g. cart became empty after the abuse purge) —
      // swap the whole drawer and sync the empty-state class.
      const cartDrawerEl = document.getElementById('CartDrawer');
      const freshDrawer = html.querySelector('#CartDrawer');
      if (cartDrawerEl && freshDrawer) cartDrawerEl.innerHTML = freshDrawer.innerHTML;
      document.querySelector('cart-drawer')?.classList.toggle('is-empty', !itemsFresh);
    }
  }

  // Sync the header cart badge with the real item count. Reward add/remove
  // goes through /cart/add|update (not the CartItems component), so nothing
  // else updates it — without this it shows a stale count after a purge.
  const bubbleTarget = document.getElementById('cart-icon-bubble');
  if (bubbleTarget && bubbleText) {
    const bubbleDoc = new DOMParser().parseFromString(bubbleText, 'text/html');
    const bubbleInner = bubbleDoc.querySelector('.shopify-section') || bubbleDoc.body;
    if (bubbleInner) bubbleTarget.innerHTML = bubbleInner.innerHTML;
  }
}

/**
 * Re-fetch and re-render the drawer when the cart changed outside our own
 * requests (e.g. a discount code was applied).
 */
async function refreshCartDrawer() {
  try {
    const root = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${root}?sections=${RENDER_SECTIONS}`);
    renderDrawerSections(await res.json());
  } catch (err) {
    console.error('[CartRewards] refreshCartDrawer error:', err);
  }
}
// cart-drawer.js applies discount codes through this; without it, it reloads the page.
window.CartRewards = { refreshCartDrawer };

// ─── Event Hooks ──────────────────────────────────────────────────────────────

/**
 * Subscribe to Dawn's pub/sub cart update event.
 * This fires after every quantity change, item remove, or add-to-cart.
 */
function initCartRewardListeners() {
  // Guard: ensure pub/sub is available (it's defined in global.js)
  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
    // Retry once scripts have loaded
    document.addEventListener('DOMContentLoaded', initCartRewardListeners, { once: true });
    return;
  }

  // Reward validation mutates the cart (gift add/remove) AND re-renders the
  // drawer, so it runs on the global cart queue (see scheduleRewardValidation).
  // Only a full cart in the event's cartData is reused (see isFullCart).
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => scheduleRewardValidation(event && event.cartData));
  // Drawer opens are hooked in CartDrawer.open() (cart-drawer.js).
}

// ─── Init ─────────────────────────────────────────────────────────────────────

// Run as soon as the script loads (deferred, so DOM is ready)
initCartRewardListeners();

/**
 * Validates that the server-applied discounts match the builder calculation.
 * Sets data-discount-valid="true" or "false" on the cart-drawer element.
 */
function validateAutomaticDiscounts(cart) {
  const eligibleHandles = [
    'your-morning-clarity',
    'your-midday-renewal',
    'your-night-unwind',
    'kashi-havan-cups',
    'moksae-intention-havan-cups'
  ];
  let eligibleCount = 0;
  let eligibleSubtotal = 0;
  
  const items = cart.items || [];
  items.forEach(item => {
    if (eligibleHandles.includes(item.handle)) {
      eligibleCount += item.quantity;
      eligibleSubtotal += item.original_line_price;
    }
  });

  // Calculate expected discount
  let expectedPct = 0;
  if (eligibleCount >= 6) expectedPct = 20;
  else if (eligibleCount >= 5) expectedPct = 15;
  else if (eligibleCount >= 4) expectedPct = 10;
  else if (eligibleCount >= 2) expectedPct = 5;

  const expectedDiscountAmount = Math.round(eligibleSubtotal * (expectedPct / 100));

  let actualDiscountAmount = 0;
  if (cart.cart_level_discount_applications) {
    cart.cart_level_discount_applications.forEach(disc => {
      actualDiscountAmount += disc.total_allocated_amount;
    });
  }

  items.forEach(item => {
    if (eligibleHandles.includes(item.handle)) {
      if (item.line_level_discount_allocations) {
        item.line_level_discount_allocations.forEach(alloc => {
          actualDiscountAmount += alloc.amount;
        });
      }
    }
  });

  // Only flag invalid when the server applied LESS than the builder promised
  // (broken promise). Other automatic discounts (e.g. "Buy 1 Get 1 15% Off",
  // the free-stand reward) legitimately push the actual total above the
  // expected tier amount — that must not strip discount styling from the UI.
  const isMatch = actualDiscountAmount + 5 >= expectedDiscountAmount;

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    cartDrawer.setAttribute('data-discount-valid', isMatch ? 'true' : 'false');
  }
}
